import React, { useState } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Chip,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  Snackbar,
  CircularProgress,
  Link as MuiLink,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  MoreVert,
  Refresh,
  Delete,
  CheckCircle,
  Block,
  MoneyOff,
  PictureAsPdf,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';

interface Invoice {
  id: string;
  number: string | null;
  customer: {
    id: string;
    email: string;
    name: string;
  } | null;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string | null;
  due_date: number | null;
  created: number;
  invoice_pdf: string | null;
}

interface ActionDialogState {
  open: boolean;
  invoice: Invoice | null;
  action: 'delete' | 'mark_paid' | 'void' | 'mark_uncollectible' | null;
}

export const InvoicesTable: React.FC = () => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [excludeInternal, setExcludeInternal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [actionDialog, setActionDialog] = useState<ActionDialogState>({
    open: false,
    invoice: null,
    action: null,
  });
  const [actionNote, setActionNote] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch invoices
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['stripe-invoices', statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', '100');

      const response = await authenticatedFetch(
        `/api/marketcompass/admin/stripe/invoices?${params.toString()}`
      );
      if (!response || !response.ok) {
        throw new Error('Failed to fetch invoices');
      }
      return response.json();
    },
  });

  // Delete draft mutation
  const deleteDraftMutation = useMutation({
    mutationFn: async ({ invoiceId, note }: { invoiceId: string; note: string }) => {
      const response = await authenticatedFetch(
        `/api/marketcompass/admin/stripe/invoices/${invoiceId}/delete`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note }),
        }
      );
      if (!response || !response.ok) {
        const error = response ? await response.json() : null;
        throw new Error(error?.error || 'Failed to delete draft invoice');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripe-invoices'] });
      setSnackbar({ open: true, message: 'Draft invoice deleted successfully', severity: 'success' });
      handleActionDialogClose();
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    },
  });

  // Mark paid mutation
  const markPaidMutation = useMutation({
    mutationFn: async ({ invoiceId, note }: { invoiceId: string; note: string }) => {
      const response = await authenticatedFetch(
        `/api/marketcompass/admin/stripe/invoices/${invoiceId}/mark-paid`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note }),
        }
      );
      if (!response || !response.ok) {
        const error = response ? await response.json() : null;
        throw new Error(error?.error || 'Failed to mark invoice as paid');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripe-invoices'] });
      setSnackbar({ open: true, message: 'Invoice marked as paid successfully', severity: 'success' });
      handleActionDialogClose();
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    },
  });

  // Void mutation
  const voidMutation = useMutation({
    mutationFn: async ({ invoiceId, note }: { invoiceId: string; note: string }) => {
      const response = await authenticatedFetch(
        `/api/marketcompass/admin/stripe/invoices/${invoiceId}/void`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note }),
        }
      );
      if (!response || !response.ok) {
        const error = response ? await response.json() : null;
        throw new Error(error?.error || 'Failed to void invoice');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripe-invoices'] });
      setSnackbar({ open: true, message: 'Invoice voided successfully', severity: 'success' });
      handleActionDialogClose();
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    },
  });

  // Mark uncollectible mutation
  const markUncollectibleMutation = useMutation({
    mutationFn: async ({ invoiceId, note }: { invoiceId: string; note: string }) => {
      const response = await authenticatedFetch(
        `/api/marketcompass/admin/stripe/invoices/${invoiceId}/mark-uncollectible`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note }),
        }
      );
      if (!response || !response.ok) {
        const error = response ? await response.json() : null;
        throw new Error(error?.error || 'Failed to mark invoice as uncollectible');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripe-invoices'] });
      setSnackbar({ open: true, message: 'Invoice marked as uncollectible successfully', severity: 'success' });
      handleActionDialogClose();
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    },
  });

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  const handleActionClick = (action: 'delete' | 'mark_paid' | 'void' | 'mark_uncollectible') => {
    if (selectedInvoice) {
      setActionDialog({
        open: true,
        invoice: selectedInvoice,
        action,
      });
    }
    handleMenuClose();
  };

  const handleActionConfirm = () => {
    if (!actionDialog.invoice || !actionDialog.action) return;

    const params = {
      invoiceId: actionDialog.invoice.id,
      note: actionNote,
    };

    switch (actionDialog.action) {
      case 'delete':
        deleteDraftMutation.mutate(params);
        break;
      case 'mark_paid':
        markPaidMutation.mutate(params);
        break;
      case 'void':
        voidMutation.mutate(params);
        break;
      case 'mark_uncollectible':
        markUncollectibleMutation.mutate(params);
        break;
    }
  };

  const handleActionDialogClose = () => {
    setActionDialog({ open: false, invoice: null, action: null });
    setActionNote('');
  };

  const getActionDialogContent = () => {
    switch (actionDialog.action) {
      case 'delete':
        return {
          title: 'Delete Draft Invoice?',
          description: 'This will permanently delete the draft invoice. This action cannot be undone.',
          buttonText: 'Delete Draft',
          buttonColor: 'error' as const,
        };
      case 'mark_paid':
        return {
          title: 'Mark Invoice as Paid?',
          description: 'This will mark the invoice as paid. Use this when payment was received outside of Stripe.',
          buttonText: 'Mark as Paid',
          buttonColor: 'success' as const,
        };
      case 'void':
        return {
          title: 'Void Invoice?',
          description: 'This will void the invoice. Voided invoices cannot be reopened. This action is typically used when an invoice was issued in error.',
          buttonText: 'Void Invoice',
          buttonColor: 'error' as const,
        };
      case 'mark_uncollectible':
        return {
          title: 'Mark Invoice as Uncollectible?',
          description: 'This will mark the invoice as uncollectible. Use this when you have determined the invoice cannot be collected.',
          buttonText: 'Mark Uncollectible',
          buttonColor: 'warning' as const,
        };
      default:
        return {
          title: '',
          description: '',
          buttonText: 'Confirm',
          buttonColor: 'primary' as const,
        };
    }
  };

  // Format date
  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return '—';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  // Get status color
  const getStatusColor = (status: string | null): 'success' | 'default' | 'error' | 'warning' => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'draft':
        return 'default';
      case 'open':
        return 'warning';
      case 'void':
      case 'uncollectible':
        return 'error';
      default:
        return 'default';
    }
  };

  // Filter invoices based on excludeInternal checkbox
  const filteredInvoices = (data?.data?.invoices || []).filter(
    (invoice: Invoice) =>
      !excludeInternal || !invoice.customer?.email?.toLowerCase().includes('younge')
  );

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: 'Invoice #',
      width: 150,
      renderCell: (params: GridRenderCellParams<Invoice>) => (
        params.row.number ? (
          <MuiLink
            href={`https://dashboard.stripe.com/invoices/${params.row.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {params.row.number}
          </MuiLink>
        ) : (
          'Draft'
        )
      ),
    },
    {
      field: 'customer',
      headerName: 'Customer',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<Invoice>) => (
        <Box>
          <Box>{params.row.customer?.email || 'N/A'}</Box>
          {params.row.customer?.name && (
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {params.row.customer.name}
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: 'amount_due',
      headerName: 'Amount',
      width: 120,
      renderCell: (params: GridRenderCellParams<Invoice>) => (
        formatCurrency(params.row.amount_due, params.row.currency)
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params: GridRenderCellParams<Invoice>) => (
        <Chip
          label={params.row.status || 'N/A'}
          color={getStatusColor(params.row.status)}
          size="small"
        />
      ),
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      width: 120,
      renderCell: (params: GridRenderCellParams<Invoice>) => formatDate(params.row.due_date),
    },
    {
      field: 'created',
      headerName: 'Created',
      width: 120,
      renderCell: (params: GridRenderCellParams<Invoice>) => formatDate(params.row.created),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Invoice>) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuClick(e, params.row)}
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  const dialogContent = getActionDialogContent();
  const isProcessing = deleteDraftMutation.isPending || markPaidMutation.isPending ||
                       voidMutation.isPending || markUncollectibleMutation.isPending;

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
          <MenuItem value="void">Void</MenuItem>
          <MenuItem value="uncollectible">Uncollectible</MenuItem>
        </TextField>

        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>

        <FormControlLabel
          control={
            <Checkbox
              checked={excludeInternal}
              onChange={(e) => setExcludeInternal(e.target.checked)}
              size="small"
            />
          }
          label="Exclude internal invoices"
        />
      </Box>

      {/* Table */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredInvoices}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          disableRowSelectionOnClick
        />
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleActionClick('delete')} disabled={selectedInvoice?.status !== 'draft'}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Draft</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleActionClick('mark_paid')} disabled={selectedInvoice?.status !== 'open'}>
          <ListItemIcon>
            <CheckCircle fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mark as Paid</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleActionClick('void')} disabled={selectedInvoice?.status !== 'open'}>
          <ListItemIcon>
            <Block fontSize="small" />
          </ListItemIcon>
          <ListItemText>Void Invoice</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleActionClick('mark_uncollectible')} disabled={selectedInvoice?.status !== 'open'}>
          <ListItemIcon>
            <MoneyOff fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mark Uncollectible</ListItemText>
        </MenuItem>
        {selectedInvoice?.invoice_pdf && (
          <MenuItem
            component="a"
            href={selectedInvoice.invoice_pdf}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ListItemIcon>
              <PictureAsPdf fontSize="small" />
            </ListItemIcon>
            <ListItemText>View PDF</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={handleActionDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{dialogContent.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogContent.description}</DialogContentText>

          {actionDialog.invoice && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Box><strong>Invoice:</strong> {actionDialog.invoice.number || 'Draft'}</Box>
              <Box><strong>Customer:</strong> {actionDialog.invoice.customer?.email}</Box>
              <Box><strong>Amount:</strong> {formatCurrency(actionDialog.invoice.amount_due, actionDialog.invoice.currency)}</Box>
            </Box>
          )}

          <TextField
            label="Internal Note (optional)"
            multiline
            rows={3}
            fullWidth
            value={actionNote}
            onChange={(e) => setActionNote(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="Add an internal note about this action..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleActionDialogClose}>Cancel</Button>
          <Button
            onClick={handleActionConfirm}
            color={dialogContent.buttonColor}
            variant="contained"
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={16} /> : null}
          >
            {dialogContent.buttonText}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
